import { useEffect, useRef, useState } from 'react';
import { IonActionSheet, IonButton, IonContent, IonDatetime, IonIcon, IonModal, IonPage, IonSpinner } from '@ionic/react';
import {
  addOutline,
  arrowForwardOutline,
  carSportOutline,
  chevronBackOutline,
  linkOutline,
  peopleOutline,
  personOutline,
  homeOutline,
  timeOutline,
  trashOutline,
} from 'ionicons/icons';
import { MapPicker } from '../components/MapPicker';
import { PlaceSelector } from '../components/PlaceSelector';
import { api } from '../lib/api';
import { connections as connectionsApi } from '../lib/connections';
import { getDeviceName } from '../lib/device';
import { joinLink } from '../lib/share';
import { listSavedRides, touchSavedRide } from '../lib/savedrides';
import { getCurrentPosition } from '../lib/tracking';
import type { Connection, Coords, Ride, SavedRide } from '../lib/types';
import type { Session } from '../lib/session';

interface Props {
  /** Gesetzt, wenn die Fahrt über einen Kontakt gestartet wird - dann kein
   *  Code, sondern direkter Push an den Kontakt. */
  contact?: Connection | null;
  onCreated: (session: Session, ride: Ride) => void;
  onBack: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}

// Handgriff, Ziel-Buttons, die Orte-Schnellauswahl ("Mein Standort" /
// "Ort speichern") samt Überschrift "Gespeicherte Orte" - mehr zeigt das
// Sheet im eingeklappten Zustand nicht. Die Orte-Chips selbst und
// gespeicherte Fahrten liegen darunter und kommen erst durchs Hochziehen
// zum Vorschein.
const SHEET_MIN = 248;
// Antippen des Griffs springt hierhin - wie das Halb-Stadium bei den
// Bottom-Sheets von Google Maps & Co.
const SHEET_EXPANDED_RATIO = 0.4;

function sheetMaxHeight(): number {
  return window.innerHeight - 72;
}

function formatClockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/**
 * `ion-datetime` liest ISO-Strings rein textlich und ignoriert die Zeitzone -
 * `toISOString()` wuerde also die UTC-Stunde in den Picker schreiben und die
 * einmal gewaehlte Zeit beim naechsten Oeffnen um den Zeitzonen-Versatz
 * verschieben. Deshalb ein lokaler ISO-String ohne Zonenangabe.
 */
function toPickerValue(ms: number): string {
  const date = new Date(ms);
  const pad = (value: number) => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
  );
}

export function DriverSetup({ contact, onCreated, onBack, onError, onNotice }: Props) {
  const [origin, setOrigin] = useState<Coords | null>(null);
  const [pickup, setPickup] = useState<Coords | null>(null);
  // Welcher der beiden Punkte gerade auf der Karte und in der Schnellauswahl
  // bearbeitet wird - nur relevant, solange es überhaupt einen Abholpunkt gibt.
  const [editingTarget, setEditingTarget] = useState<'origin' | 'pickup'>('origin');
  const [activeContact, setActiveContact] = useState<Connection | null>(contact ?? null);
  const [savedRides, setSavedRides] = useState<SavedRide[]>(() => (contact ? [] : listSavedRides()));
  const [contacts, setContacts] = useState<Connection[]>([]);
  const [choosing, setChoosing] = useState(false);
  const [creating, setCreating] = useState(false);
  // Circa-Ankunftszeit fuer den Mitfahrer - optional, ueber den Time Picker
  // neben "Fahrt anlegen" gesetzt.
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const timePicker = useRef<HTMLIonDatetimeElement>(null);

  const [sheetHeight, setSheetHeight] = useState(SHEET_MIN);
  const drag = useRef<{ startY: number; startHeight: number } | null>(null);
  // Immer der aktuelle Wert, auch fuer die nativen Touch-Listener unten, die
  // nur einmal registriert werden und sonst eine veraltete Hoehe saehen.
  const sheetHeightRef = useRef(sheetHeight);
  sheetHeightRef.current = sheetHeight;
  const sheetScrollRef = useRef<HTMLDivElement>(null);

  // Der eigene Standort ist fast immer der richtige Startpunkt - direkt anbieten.
  useEffect(() => {
    let cancelled = false;
    getCurrentPosition()
      .then((fix) => {
        if (!cancelled) setOrigin((current) => current ?? { lat: fix.lat, lng: fix.lng });
      })
      .catch(() => {
        // Kein Standort: der Nutzer setzt den Punkt eben auf der Karte.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Für die Auswahl nach "Fahrt anlegen" - nur nötig, wenn noch kein Kontakt
  // feststeht (kam die Fahrt schon mit einem Kontakt herein, entfällt die Wahl).
  useEffect(() => {
    if (contact) return;
    void connectionsApi
      .list()
      .then(({ connections: list }) => setContacts(Array.isArray(list) ? list : []))
      .catch(() => setContacts([]));
  }, [contact]);

  function addPickup() {
    // Startpunkt als Vorschlag - direkt ein Marker zum Verschieben statt
    // einer leeren Karte.
    setPickup((current) => current ?? origin ?? null);
    setEditingTarget('pickup');
  }

  function removePickup() {
    setPickup(null);
    setEditingTarget('origin');
  }

  /**
   * Stehen eigene Knoepfe im `buttons`-Slot, uebernimmt `ion-datetime` die
   * Auswahl nicht mehr von selbst - erst `confirm()` schreibt sie in den Wert
   * und loest `ionChange` aus. Ohne diesen Aufruf bliebe die gewaehlte Zeit im
   * Picker haengen und der Knopf zeigte weiter nur das Uhr-Symbol.
   *
   * Wurde am Rad gar nicht gedreht, liefert `confirm()` einen leeren Wert -
   * dann bleibt es dabei, dass keine Zeit gesetzt ist.
   */
  async function confirmTime() {
    await timePicker.current?.confirm();
    setTimePickerOpen(false);
  }

  async function clearTime() {
    setEstimatedTime(null);
    // Setzt auch das Rad zurueck, damit beim naechsten Oeffnen nicht die
    // eben entfernte Zeit wieder vorausgewaehlt ist.
    await timePicker.current?.reset();
    setTimePickerOpen(false);
  }

  function useSavedRide(ride: SavedRide) {
    setSavedRides(touchSavedRide(ride.id));
    setOrigin(ride.origin);
    setPickup(ride.pickup);
    setEditingTarget('origin');
    if (!ride.contactId) {
      setActiveContact(null);
      return;
    }
    // Die Verknuepfung zum Kontakt gilt nur, solange es ihn noch gibt -
    // deshalb frisch nachschlagen statt dem gespeicherten Namen zu trauen.
    void connectionsApi
      .list()
      .then(({ connections: list }) => setActiveContact(list.find((entry) => entry.id === ride.contactId) ?? null))
      .catch(() => setActiveContact(null));
  }

  function onHandlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    drag.current = { startY: event.clientY, startHeight: sheetHeight };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onHandlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const delta = drag.current.startY - event.clientY;
    setSheetHeight(Math.min(sheetMaxHeight(), Math.max(SHEET_MIN, drag.current.startHeight + delta)));
  }

  function onHandlePointerUp() {
    drag.current = null;
  }

  // Antippen (statt Ziehen) des Griffs: auf und zu wie bei jedem anderen
  // Bottom-Sheet - kein Wischen noetig, um mehr als die zwei Buttons zu sehen.
  function onHandleClick() {
    const expanded = Math.round(window.innerHeight * SHEET_EXPANDED_RATIO);
    setSheetHeight((current) => (current < expanded ? expanded : SHEET_MIN));
  }

  // Scrollen im Sheet waechst erst das Panel, bevor der Inhalt selbst
  // scrollt - und schrumpft es wieder, wenn ganz oben weiter nach oben
  // gescrollt wird. Trackpad/Maus-Rad reicht ueber `onWheel`; fuer Touch
  // braucht es echte, nicht-passive Listener (s. Effekt unten), sonst
  // gewinnt immer das native Scrollen der Liste.
  function onSheetWheel(event: React.WheelEvent<HTMLDivElement>) {
    const maxHeight = sheetMaxHeight();
    const atTop = event.currentTarget.scrollTop <= 0;
    if (event.deltaY > 0 && sheetHeight < maxHeight) {
      event.preventDefault();
      setSheetHeight(Math.min(maxHeight, sheetHeight + event.deltaY));
    } else if (event.deltaY < 0 && atTop && sheetHeight > SHEET_MIN) {
      event.preventDefault();
      setSheetHeight(Math.max(SHEET_MIN, sheetHeight + event.deltaY));
    }
  }

  useEffect(() => {
    const el = sheetScrollRef.current;
    if (!el) return;

    const touch = { startY: 0, startHeight: 0, resizing: false };

    function onTouchStart(event: TouchEvent) {
      touch.startY = event.touches[0].clientY;
      touch.startHeight = sheetHeightRef.current;
      touch.resizing = false;
    }

    function onTouchMove(event: TouchEvent) {
      if (!el) return;
      const deltaY = touch.startY - event.touches[0].clientY;
      const maxHeight = sheetMaxHeight();
      if (!touch.resizing) {
        // Erst eingreifen, wenn der Rand erreicht ist - sonst scrollt die
        // Liste ganz normal weiter.
        if (deltaY > 0 && sheetHeightRef.current < maxHeight) touch.resizing = true;
        else if (deltaY < 0 && el.scrollTop <= 0 && sheetHeightRef.current > SHEET_MIN) touch.resizing = true;
        else return;
      }
      event.preventDefault();
      setSheetHeight(Math.min(maxHeight, Math.max(SHEET_MIN, touch.startHeight + deltaY)));
    }

    function onTouchEnd() {
      touch.resizing = false;
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  function onCreateClick() {
    if (!origin) {
      onError('Bitte setze zuerst deinen Startpunkt auf der Karte.');
      return;
    }
    // Steht der Kontakt schon fest (Vorlage oder direkter Einstieg über
    // Kontakte), gibt es nichts mehr zu wählen.
    if (activeContact) {
      void create(activeContact, false);
    } else {
      setChoosing(true);
    }
  }

  async function create(withContact: Connection | null, copyLink: boolean) {
    if (!origin) return;
    setCreating(true);
    try {
      let created: { ride: Ride; token: string };
      if (withContact) {
        const { ride, token } = await connectionsApi.startRide(withContact.id, origin, estimatedTime);
        created = { ride, token };
      } else {
        const { ride, token } = await api.createRide({
          driverName: getDeviceName().trim() || 'Fahrer',
          origin,
          pickup: pickup ?? undefined,
          estimatedTime,
        });
        created = { ride, token };
        if (copyLink) {
          // Nicht awaiten: haengt der Zwischenspeicher (Berechtigungsdialog,
          // Sonderfall in manchen Browsern), darf das nicht die Navigation
          // zur angelegten Fahrt blockieren - der Code steht dort so oder so.
          navigator.clipboard.writeText(joinLink(ride.code)).then(
            () => onNotice('Link kopiert.'),
            () => {
              // Kein Zwischenspeicher erreichbar - der Code steht trotzdem auf der naechsten Seite.
            },
          );
        }
      }

      // Kam die Fahrt über einen Kontakt, kennt `startRide` keinen
      // Abholpunkt - den aus der gespeicherten Fahrt tragen wir hier nach.
      // Der Mitfahrer kann ihn weiterhin selbst ändern.
      if (withContact && pickup) {
        try {
          const { ride: withPickup } = await api.setPickup(created.ride.id, created.token, pickup);
          created = { ride: withPickup, token: created.token };
        } catch {
          // Nicht schlimm - der Mitfahrer setzt den Punkt dann selbst.
        }
      }

      onCreated({ rideId: created.ride.id, token: created.token, role: 'driver' }, created.ride);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Fahrt konnte nicht angelegt werden.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        {/* Bolt-Layout: die Karte ist der erste Eindruck. Das Sheet startet
            eingeklappt und zeigt nur die Standort-Buttons plus Hauptknopf -
            hochziehen legt die gespeicherten Fahrten frei. */}
        <div className="r2f-setup">
          <div className="r2f-setup__map">
            <MapPicker
              value={editingTarget === 'origin' ? origin : pickup}
              onChange={editingTarget === 'origin' ? setOrigin : setPickup}
              kind={editingTarget}
              secondary={editingTarget === 'origin' ? pickup : origin}
              secondaryKind={editingTarget === 'origin' ? 'pickup' : 'origin'}
            />
          </div>

          <div className="r2f-setup__topbar">
            <IonButton size="small" fill="solid" className="r2f-mapbtn" onClick={onBack} aria-label="Zurück">
              <IonIcon slot="icon-only" icon={chevronBackOutline} />
            </IonButton>
          </div>

          <div className="r2f-setup__sheet" style={{ height: sheetHeight }}>
            <div
              className="r2f-setup__handle"
              role="button"
              aria-label="Sheet-Höhe ändern - antippen zum Auf- und Zuklappen, ziehen zum freien Einstellen"
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              onClick={onHandleClick}
            >
              <span className="r2f-setup__handlebar" aria-hidden="true" />
            </div>

            <div className="r2f-setup__sheetscroll" ref={sheetScrollRef} onWheel={onSheetWheel}>
              {pickup ? (
                <div className="r2f-row" style={{ gap: 8, marginBottom: 10 }}>
                  <IonButton
                    size="small"
                    fill={editingTarget === 'origin' ? 'solid' : 'outline'}
                    onClick={() => setEditingTarget('origin')}
                  >
                    <IonIcon slot="start" icon={homeOutline} />
                    Start
                  </IonButton>
                  <IonButton
                    size="small"
                    fill={editingTarget === 'pickup' ? 'solid' : 'outline'}
                    onClick={() => setEditingTarget('pickup')}
                  >
                    <IonIcon slot="start" icon={personOutline} />
                    Abholung
                  </IonButton>
                  <IonButton size="small" fill="clear" color="medium" onClick={removePickup} aria-label="Abholpunkt entfernen">
                    <IonIcon slot="icon-only" icon={trashOutline} />
                  </IonButton>
                </div>
              ) : (
                <IonButton size="small" fill="outline" onClick={addPickup} style={{ marginBottom: 10 }}>
                  <IonIcon slot="start" icon={addOutline} />
                  Abholpunkt hinzufügen
                </IonButton>
              )}

              <PlaceSelector
                value={editingTarget === 'origin' ? origin : pickup}
                onChange={editingTarget === 'origin' ? setOrigin : setPickup}
                onError={onError}
              />

              {!contact && savedRides.length > 0 && (
                <>
                  <h3 className="r2f-setup__sectiontitle">Gespeicherte Fahrten</h3>
                  <div className="r2f-setup__rides">
                    {savedRides.map((ride) => (
                      <button
                        key={ride.id}
                        type="button"
                        className="r2f-setup__ride"
                        onClick={() => useSavedRide(ride)}
                      >
                        <IonIcon icon={ride.contactId ? peopleOutline : carSportOutline} aria-hidden="true" />
                        <span>
                          <strong>{ride.name}</strong>
                          <em>{ride.contactLabel ? `mit ${ride.contactLabel}` : 'Startpunkt gespeichert'}</em>
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="r2f-actionbar r2f-row" style={{ gap: 8 }}>
              <IonButton expand="block" size="large" style={{ flex: 1 }} disabled={!origin || creating} onClick={onCreateClick}>
                {creating ? (
                  <IonSpinner name="dots" />
                ) : (
                  <>
                    {activeContact ? `Fahrt mit ${activeContact.label} starten` : 'Fahrt anlegen'}
                    <IonIcon slot="end" icon={arrowForwardOutline} />
                  </>
                )}
              </IonButton>
              <IonButton
                size="large"
                fill="outline"
                className="r2f-timebtn"
                onClick={() => setTimePickerOpen(true)}
                aria-label={estimatedTime ? `Ankunftszeit: ${formatClockTime(estimatedTime)}` : 'Ankunftszeit auswählen'}
              >
                {estimatedTime ? formatClockTime(estimatedTime) : <IonIcon slot="icon-only" icon={timeOutline} />}
              </IonButton>
            </div>
          </div>
        </div>

        <IonActionSheet
          isOpen={choosing}
          header="Wie soll dein Mitfahrer Bescheid bekommen?"
          onDidDismiss={() => setChoosing(false)}
          buttons={[
            ...contacts.map((entry) => ({
              text: `Mit ${entry.label} starten`,
              icon: peopleOutline,
              handler: () => void create(entry, false),
            })),
            { text: 'Link kopieren', icon: linkOutline, handler: () => void create(null, true) },
            { text: 'Abbrechen', role: 'cancel' },
          ]}
        />

        <IonModal isOpen={timePickerOpen} onDidDismiss={() => setTimePickerOpen(false)} className="r2f-time-modal">
          <IonDatetime
            ref={timePicker}
            presentation="time"
            value={estimatedTime ? toPickerValue(estimatedTime) : undefined}
            onIonChange={(event) => {
              const value = Array.isArray(event.detail.value) ? event.detail.value[0] : event.detail.value;
              setEstimatedTime(value ? new Date(value).getTime() : null);
            }}
          >
            <div className="r2f-time-modal__actions" slot="buttons">
              {estimatedTime && (
                <IonButton fill="clear" color="medium" onClick={clearTime}>
                  Entfernen
                </IonButton>
              )}
              <IonButton fill="clear" onClick={confirmTime}>
                Fertig
              </IonButton>
            </div>
          </IonDatetime>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}
